import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import './ralenti.css';
import ItemList from '../../components/sections/itemList';

import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement,
} from 'chart.js';

import zoomPlugin from 'chartjs-plugin-zoom';
import DateRangeSelector from '../../components/sections/dateRangeSelector';
import LargeCard from '../../components/sections/largeCard';
import FuelEstimateCard from '../../components/fuelEstimateCard';
import Loader from '../../components/loader';
import { endOfDay, filterRalentiReport, formatDate, formatDateRange, formatTime, startOfDay, subtractDays } from '../../utils/generalFunctions';
import RetryReport from '../../components/retryReport';
import ChartLegends from '../../components/chartLegends';
import { DateContext } from '../../context/dateProvider';

ChartJS.register(
    CategoryScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin,
);

const Ralenti = ({ dataReport, devices, updateDevices, progress, error, fetchReport, userConstants, updateUserConstants, isRecoveredReports }) => {
    const { startDate, endDate, today,
        yesterday,
        startActualWeek,
        endActualWeek,
        startPreviousWeek,
        endPreviousWeek,
        startActualMonth,
        endActualMonth,
        startPreviousMonth,
        endPreviousMonth,
    } = useContext(DateContext);

    const deviceLabels = devices && Array.isArray(devices) ? devices.map(device => device.label) : []; // Extrae los labels de los dispositivos
    const [isLoading, setIsLoading] = useState(true);
    const [engineData, setEngineData] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.ralentiData : []);
    const [showChartComparison, setShowChartComparison] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(deviceLabels[0] || '');
    const [showAllHigherTop, setShowAllHigherTop] = useState(false);
    const [showAllLesserTop, setShowAllLesserTop] = useState(false);
    const [showAllTopRalentiFuel, setShowAllTopRalentiFuel] = useState(false);
    const [chartDateRange, setChartDateRange] = useState(0);
    const [dateHigherTopRange, setDateHigherTopRange] = useState(0);
    const [dateLesserTopRange, setDateLesserTopRange] = useState(0);
    const [dateTopRangeRalentiFuel, setDateTopRangeRalentiFuel] = useState(0);
    const [totalData, setTotalData] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.ralentiTotalData : {});
    const [currentData, setCurrentData] = useState({});
    const [previousData, setPreviousData] = useState({});
    const [displayedHigherData, setDisplayedHigherData] = useState([]);
    const [displayedLesserData, setDisplayedLesserData] = useState([]);
    const [displayedTopRalentiFuel, setDisplayedTopRalentiFuel] = useState([]);
    const chartRef = useRef(null);
    const barChartRef = useRef(null);
    const [activeTab, setActiveTab] = useState('bar');

    // const today = new Date(endDate);
    // const yesterday = subtractDays(1, today);
    // const startActualWeek = subtractDays(6, today);
    // const endActualWeek = today;
    // const startActualMonth = subtractDays(29, today);
    // const endActualMonth = today;
    // const startPreviousWeek = subtractDays(13, today);
    // const endPreviousWeek = subtractDays(7, today, true);
    // const startPreviousMonth = subtractDays(59, today);
    // const endPreviousMonth = subtractDays(30, today, true);

    const emptyData = {
        duration: 0,
        idle: 0,
        avg_period: 0,
        avg_speed: 0,
        in_movement: 0,
        percent_in_movement: 0,
        percent_idle: 0,
        mileage: 0,
        period_count: 0,
    };

    // Mapeo de periodos actuales y previos
    const periodKeyMap = {
        0: { current: 'Hoy', previous: 'Ayer' },
        1: { current: 'Semana actual', previous: 'Semana previa' },
        2: { current: 'Mes actual', previous: 'Mes previo' },
    };

    useEffect(() => {
        if (!dataReport || dataReport.length === 0) return;

        setIsLoading(true);

        const processedData = filterRalentiReport(dataReport, startPreviousMonth, endDate);

        setEngineData(processedData);

        const accumulatedData = calculateTotal(processedData);

        setTotalData(accumulatedData);

        updateUserConstants({ "reports.ralentiData": processedData, "reports.ralentiTotalData": accumulatedData });

        setIsLoading(false);
    }, [dataReport]);

    const calculateTotal = (engineData) => {
        // Aquí es donde acumulamos los datos por periodo
        let accumulatedData = {
            "Hoy": {},
            "Ayer": {},
            "Semana actual": {},
            "Semana previa": {},
            "Mes actual": {},
            "Mes previo": {}
        };

        // Inicializamos todos los vehículos con valores 0 en cada periodo
        engineData.forEach(obj => {
            ["Hoy", "Ayer", "Semana actual", "Semana previa", "Mes actual", "Mes previo"].forEach(period => {
                Object.keys(obj).forEach(carName => {
                    if (carName !== 'date') {  // Asegurarte de que no estás procesando la fecha
                        accumulatedData[period][carName] = {
                            ...emptyData,
                            // Contadores para calcular promedios
                            avg_period_count: 0,
                            avg_speed_count: 0,
                            percent_in_movement_count: 0,
                            percent_idle_count: 0
                        };  // Asignar 0 por defecto
                    }
                });
            });
        });

        const addDataToPeriod = (period, carName, data) => {
            // Inicializar el objeto si no existe
            if (!accumulatedData[period][carName]) {
                accumulatedData[period][carName] = {
                    ...emptyData,
                    // Contadores para calcular promedios
                    avg_period_count: 0,
                    avg_speed_count: 0,
                    percent_in_movement_count: 0,
                    percent_idle_count: 0,
                };
            }

            // Acumula los datos
            if (data.duration !== ' — ') {
                accumulatedData[period][carName].duration += data.duration || 0;
            }
            if (data.idle !== ' — ') {
                accumulatedData[period][carName].idle += data.idle || 0;
            }
            if (data.in_movement !== ' — ') {
                accumulatedData[period][carName].in_movement += data.in_movement || 0;
            }
            if (data.mileage !== ' — ') {
                accumulatedData[period][carName].mileage += data.mileage || 0;
            }
            if (data.period_count !== ' — ') {
                accumulatedData[period][carName].period_count += data.period_count || 0;
            }
            if (data.avg_period !== ' — ') {
                accumulatedData[period][carName].avg_period += data.avg_period || 0;
            }
            if (data.avg_speed !== ' — ') {
                accumulatedData[period][carName].avg_speed += data.avg_speed || 0;
            }
            if (data.percent_in_movement !== ' — ') {
                accumulatedData[period][carName].percent_in_movement += data.percent_in_movement || 0;
            }
            if (data.percent_idle !== ' — ') {
                accumulatedData[period][carName].percent_idle += data.percent_idle || 0;
            }

            // Suma los valores para los que necesitamos calcular el promedio
            if (accumulatedData[period][carName].duration !== ' — ') {
                if (data.avg_period !== ' — ') {
                    accumulatedData[period][carName].avg_period_count += 1;
                }
                if (data.avg_speed !== ' — ') {
                    accumulatedData[period][carName].avg_speed_count += 1;
                }
                if (data.percent_in_movement !== ' — ') {
                    accumulatedData[period][carName].percent_in_movement_count += 1;
                }
                if (data.percent_idle !== ' — ') {
                    accumulatedData[period][carName].percent_idle_count += 1;
                }
            }
        };

        // Procesamos los datos
        engineData.forEach(obj => {
            const entryDate = new Date(obj.date);
            Object.keys(obj).forEach(carName => {
                if (carName !== 'date') {
                    const data = obj[carName] || {
                        ...emptyData,
                        // Contadores para calcular promedios
                        avg_period_count: 0,
                        avg_speed_count: 0,
                        percent_in_movement_count: 0,
                        percent_idle_count: 0,
                    };

                    if (entryDate.toDateString() === today.toDateString()) {
                        addDataToPeriod("Hoy", carName, data);
                    } else if (entryDate.toDateString() === yesterday.toDateString()) {
                        addDataToPeriod("Ayer", carName, data);
                    }

                    if (entryDate >= startActualWeek && entryDate <= today) {
                        addDataToPeriod("Semana actual", carName, data);
                    }

                    if (entryDate >= startActualMonth && entryDate <= today) {
                        addDataToPeriod("Mes actual", carName, data);
                    }

                    if (entryDate >= startPreviousWeek && entryDate <= endPreviousWeek) {
                        addDataToPeriod("Semana previa", carName, data);
                    }

                    if (entryDate >= startPreviousMonth && entryDate <= endPreviousMonth) {
                        addDataToPeriod("Mes previo", carName, data);
                    }
                }
            });
        });

        // Después de acumular, calcula los promedios
        Object.keys(accumulatedData).forEach(period => {
            Object.keys(accumulatedData[period]).forEach(carName => {
                const data = accumulatedData[period][carName];
                const periodSeconds = period === 'Hoy' || period === 'Ayer' ? 86400 : period === 'Semana actual' || period === 'Semana previa' ? 604800 : 2592000;

                // Calcula el promedio solo si el contador es mayor a 0
                if (data.avg_period_count > 0) {
                    data.avg_period = data.avg_period / data.avg_period_count;
                }
                if (data.avg_speed_count > 0) {
                    data.avg_speed = data.avg_speed / data.avg_speed_count;
                }
                if (data.duration > 0) {
                    data.percent_in_movement = (data.in_movement / data.duration) * 100;
                    data.percent_idle = (data.idle / data.duration) * 100;
                    data.engine_off_time = periodSeconds - data.duration;
                } else {
                    data.percent_in_movement = 0;
                    data.percent_idle = 0;
                    data.engine_off_time = 0;
                }
            });
        });

        return accumulatedData;
    };

    useEffect(() => {
        const newTotalData = calculateTotal(engineData);
        setTotalData(newTotalData);
    }, [today, yesterday, startActualWeek, endActualWeek, startPreviousWeek, endPreviousWeek]);

    const vibrantColors = [];

    // Llenar el array de colores con los colores de los dispositivos en `devices`
    devices.forEach(item => {
        vibrantColors.push(item.color);
    });

    // useEffect para actualizar los datasets basados en `devices`
    useEffect(() => {
        // Obtener las fechas actuales y las fechas para el periodo de comparación
        const dateLabels = filteredData.map(item => formatDate(item.date));
        const previousDateLabels = filteredPreviousData.map(item => formatDate(item.date));

        // Crear un array para almacenar los datasets de cada vehículo
        const updatedDatasets = [];

        // Iterar sobre cada vehículo en `devices`
        devices.forEach((device, index) => {
            const vehicleName = device.label;  // El nombre del vehículo
            const vehicleColor = device.color; // El color asignado al vehículo

            // Dataset para "En movimiento" del vehículo actual
            updatedDatasets.push({
                label: vehicleName,  // Etiqueta para la leyenda
                vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                parameter: 'En movimiento',
                data: dateLabels.map(date => getVehicleDataForDate(date, vehicleName).in_movement || 0),  // Obtener los datos de "En movimiento"
                borderColor: vehicleColor,  // Asignar el color del vehículo
                backgroundColor: vehicleColor,  // Asignar el color de fondo del vehículo
                borderWidth: 2,
                stack: `${vehicleName}-actual`,  // Stack para el periodo actual
                type: 'bar',
                hidden: !device.visible  // Sincronizar con la visibilidad del dispositivo
            });

            // Dataset para "En ralentí" del vehículo actual
            updatedDatasets.push({
                label: `${vehicleName} (En ralentí)`,  // Etiqueta para la leyenda
                vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                parameter: 'En ralentí',
                data: dateLabels.map(date => getVehicleDataForDate(date, vehicleName).idle || 0),  // Obtener los datos de "En ralentí"
                borderColor: adjustColor(vehicleColor, -20),  // Color más oscuro para "En ralentí"
                backgroundColor: adjustColor(vehicleColor, -20),  // Fondo más oscuro
                borderWidth: 2,
                stack: `${vehicleName}-actual`,  // Stack para el periodo actual
                type: 'bar',
                hidden: !device.visible  // Sincronizar con la visibilidad del dispositivo
            });

            if (showChartComparison) {
                // Dataset para "En movimiento" del vehículo en el periodo anterior
                updatedDatasets.push({
                    label: `${vehicleName} (Anterior)`,  // Etiqueta para la leyenda
                    vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                    parameter: 'En movimiento',
                    data: previousDateLabels.map(date => getVehicleDataForDate(date, vehicleName).in_movement || 0),  // Datos de comparación para "En movimiento"
                    borderColor: adjustColor(vehicleColor, 50),  // Color más claro para la comparación
                    backgroundColor: 'rgba(0, 0, 0, 0)',  // Fondo transparente para la comparación
                    borderDash: [5, 5],
                    borderWidth: 2,
                    stack: `${vehicleName}-anterior`,  // Stack para el periodo de comparación
                    type: 'bar',
                    hidden: !device.visible  // Sincronizar con la visibilidad del dispositivo
                });

                // Dataset para "En ralentí" del vehículo en el periodo anterior
                updatedDatasets.push({
                    label: `${vehicleName} (Anterior)`,  // Etiqueta para la leyenda
                    vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                    parameter: 'En ralentí',
                    data: previousDateLabels.map(date => getVehicleDataForDate(date, vehicleName).idle || 0),  // Datos de comparación para "En ralentí"
                    borderColor: adjustColor(vehicleColor, 30),  // Color ajustado para la comparación
                    backgroundColor: 'rgba(0, 0, 0, 0)',  // Fondo transparente para la comparación
                    borderDash: [5, 5],
                    borderWidth: 2,
                    stack: `${vehicleName}-anterior`,  // Stack para el periodo de comparación
                    type: 'bar',
                    hidden: !device.visible  // Sincronizar con la visibilidad del dispositivo
                });
            }
        });

        // Actualizar los datasets en la gráfica
        if (barChartRef.current) {
            barChartRef.current.data.datasets = updatedDatasets;
            barChartRef.current.update();
        }
    }, [devices]);

    // const updateChart = () => {
    //     if (!barChartRef.current || !barChartRef.current.chartInstance) return;

    //     const updatedData = generateStackedBarDataByDate();

    //     barChartRef.current.chartInstance.data.datasets = updatedData.datasets;
    //     barChartRef.current.chartInstance.labels = updatedData.labels;
    //     barChartRef.current.chartInstance.update();
    // };

    // // Llamar a `updateChart` cuando se necesite actualizar la gráfica sin re-renderizar
    // useEffect(() => {
    //     updateChart();
    // }, [devices]);


    const getDeviceDataForPeriod = (period) => {
        return totalData[period]?.[selectedDevice] || emptyData;
    };

    const filterDataByRange = (range, isPrevious = false) => {
        const filteredData = [];

        let startDate2, endDate2;

        // Elegir fechas según el rango y si es el periodo anterior o actual
        switch (range) {
            case 0:
                startDate2 = isPrevious ? startOfDay(yesterday) : startOfDay(today);
                endDate2 = isPrevious ? endOfDay(yesterday) : endOfDay(today);
                break;
            case 1:
                startDate2 = isPrevious ? startOfDay(startPreviousWeek) : startOfDay(startActualWeek);
                endDate2 = isPrevious ? endOfDay(endPreviousWeek) : startOfDay(endActualWeek);
                break;
            case 2:
                startDate2 = isPrevious ? startOfDay(startPreviousMonth) : startOfDay(startActualMonth);
                endDate2 = isPrevious ? endOfDay(endPreviousMonth) : endOfDay(endActualMonth);
                break;
            default:
                startDate2 = startOfDay(new Date(startDate));
                endDate2 = endOfDay(today);
        }

        // Filtrar los datos de acuerdo al rango seleccionado
        engineData.forEach(item => {
            if (new Date(item.date) >= startDate2 && new Date(item.date) <= endDate2) {
                filteredData.push(item);
            }
        });

        return filteredData;
    };


    const getVehicleDataForDate = (date, vehicleLabel) => {
        // Busca la entrada correspondiente a la fecha en engineData
        const dayData = engineData.find(day => day.date === date);

        // Si hay datos del día, devuelve los datos del vehículo, de lo contrario devuelve emptyData
        return dayData ? dayData[vehicleLabel] || emptyData : emptyData;
    };

    const adjustColor = (color, amount) => {
        let colorValue = color.startsWith('#') ? color.slice(1) : color;
        let num = parseInt(colorValue, 16);

        let r = ((num >> 16) + amount);
        let g = (((num >> 8) & 0x00FF) + amount);
        let b = ((num & 0x0000FF) + amount);

        r = Math.min(255, Math.max(0, r));
        g = Math.min(255, Math.max(0, g));
        b = Math.min(255, Math.max(0, b));

        // Colocamos los paréntesis para aclarar el orden de las operaciones
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    };

    const filteredData = filterDataByRange(chartDateRange);
    const filteredPreviousData = filterDataByRange(chartDateRange, true);

    const generateStackedBarDataByDate = () => {
        // 1. Filtrar solo los días con datos visibles
        const validDays = filteredData.filter(day =>
            devices.some(device => device.visible && day[device.label] && (
                day[device.label].idle > 0 || day[device.label].in_movement > 0
            ))
        );

        const validDates = validDays.map(item => item.date);
        const labels = validDates.map(date => formatDate(date));
        const previousLabels = filteredPreviousData.map(item => formatDate(item.date));
        // const dateLabels = filteredData.map(item => (item.date));
        // const previousDateLabels = filteredPreviousData.map(item => (item.date));  // Generamos las fechas del periodo anterior

        // Crear un dataset para cada vehículo
        const datasets = [];

        devices.forEach((device) => {
            if (!device.visible) return;
            const vehicleName = device.label;  // El nombre del vehículo
            const vehicleColor = device.color; // El color del vehículo

            // Dataset para "En movimiento" del vehículo actual
            datasets.push({
                label: vehicleName,  // Usamos solo el nombre del vehículo para la leyenda
                vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                parameter: 'En movimiento',
                data: validDates.map(date => getVehicleDataForDate(date, vehicleName).in_movement || 0),
                borderColor: vehicleColor,  // Asignar el color del vehículo
                backgroundColor: vehicleColor,  // Asignar el color del vehículo
                borderWidth: 2,
                stack: `${vehicleName}-actual`,  // Stack para el periodo actual
                type: 'bar',
                hidden: !device.visible
            });

            // Dataset para "En ralentí" del vehículo actual
            datasets.push({
                label: `${vehicleName} (En ralentí)`,  // Usamos solo el nombre del vehículo para la leyenda
                vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                parameter: 'En ralentí',
                data: validDates.map(date => getVehicleDataForDate(date, vehicleName).idle || 0),
                borderColor: adjustColor(vehicleColor, -20),  // Color más oscuro para "En ralentí"
                backgroundColor: adjustColor(vehicleColor, -20),  // Color más oscuro para "En ralentí"
                borderWidth: 2,
                stack: `${vehicleName}-actual`,  // Stack para el periodo actual
                type: 'bar',
                hidden: !device.visible
            });

            if (showChartComparison) {
                // Dataset para "En movimiento" del vehículo en el periodo anterior
                datasets.push({
                    label: `${vehicleName} (Anterior)`,  // Nombre del vehículo para la comparación
                    vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                    parameter: 'En movimiento',
                    data: previousLabels.map(date => getVehicleDataForDate(date, vehicleName).in_movement || 0),  // Obtener datos del periodo anterior
                    borderColor: adjustColor(vehicleColor, 50),  // Color más claro para la comparación
                    backgroundColor: 'rgba(0, 0, 0, 0)',  // Color más claro para la comparación
                    borderDash: [5, 5],
                    borderWidth: 2,
                    stack: `${vehicleName}-anterior`,  // Stack separado para el periodo de comparación
                    type: 'bar',
                    hidden: !device.visible
                });

                // Dataset para "En ralentí" del vehículo en el periodo anterior
                datasets.push({
                    label: `${vehicleName} (Anterior)`,  // Nombre del vehículo para la comparación
                    vehicle: vehicleName,  // Propiedad personalizada para identificar el vehículo
                    parameter: 'En ralentí',
                    data: previousLabels.map(date => getVehicleDataForDate(date, vehicleName).idle || 0),  // Obtener datos del periodo anterior
                    borderColor: adjustColor(vehicleColor, 30),  // Color ajustado para la comparación
                    backgroundColor: 'rgba(0, 0, 0, 0)',  // Color ajustado para la comparación
                    borderDash: [5, 5],
                    borderWidth: 2,
                    stack: `${vehicleName}-anterior`,  // Stack separado para el periodo de comparación
                    type: 'bar',
                    hidden: !device.visible
                });
            }
        });

        return {
            labels,  // Fechas en el eje x para el periodo actual
            datasets   // Vehículos con sus propios stacks
        };
    };

    const stackedBarOptionsByDate = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
        },
        scales: {
            x: {
                stacked: true,
                title: {
                    display: false,
                    text: 'Fecha',
                    font: { size: 16, weight: 'bold' },
                }
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: 'Duración (minutos)',  // Cambia el título del eje "y" a minutos
                    font: { size: 16, weight: 'bold' },
                },
                ticks: {
                    // Aquí es donde transformamos los valores de segundos a minutos
                    callback: function (value, index, values) {
                        // Convertir de segundos a minutos
                        const minutes = value / 60;
                        return `${minutes.toFixed(0)} min`;  // Mostrar con dos decimales
                    }
                }
            }
        },
        plugins: {
            zoom: {
                pan: {
                    enabled: true,  // Habilitar paneo
                    mode: 'x',      // Solo permitir el paneo en el eje X (horizontal)
                    threshold: 5,
                    modifierKey: 'ctrl'
                },
                zoom: {
                    wheel: {
                        enabled: chartDateRange !== 0,  // Habilitar zoom solo si no es "Hoy"
                        threshold: 5,
                        modifierKey: 'ctrl'
                    },
                    pinch: {
                        enabled: true,  // Habilitar zoom con gestos táctiles
                        threshold: 2,
                    },
                    mode: 'x',  // Hacer zoom solo en el eje X
                }
            },
            legend: {
                display: false,
                position: 'bottom',
                onHover: (event) => {
                    event.native.target.style.cursor = 'pointer';
                },
                onLeave: (event) => {
                    event.native.target.style.cursor = 'default';
                },
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    pointStyle: 'circle',
                    boxWidth: 9,        // Ajusta el ancho de la caja del punto
                    boxHeight: 9,
                    font: {
                        size: 14
                    },
                    filter: (legendItem) => !legendItem.text.includes('(Anterior)') && !legendItem.text.includes('(En ralentí)'),
                },
                onClick: (e, legendItem, legend) => {
                    const chart = legend.chart;
                    const datasetIndex = legendItem.datasetIndex;
                    const dataset = chart.data.datasets[datasetIndex];
                    const deviceName = legendItem.text.replace(' (Anterior)', '');

                    // Alternar visibilidad del dataset
                    const isHidden = !dataset.hidden;
                    dataset.hidden = isHidden;

                    // Actualiza el estado de `devices` para reflejar el cambio
                    const updatedDevices = devices.map(device =>
                        device.label === deviceName ? { ...device, visible: !isHidden } : device
                    );

                    updateDevices(updatedDevices);

                    // Alternar el estado "hidden" de todas las barras del vehículo
                    chart.data.datasets.forEach((dataset, index) => {
                        if (dataset.vehicle === deviceName) {
                            const meta = chart.getDatasetMeta(index);
                            meta.hidden = !isHidden;  // Si todas las barras están ocultas, mostrarlas. Si no, ocultarlas.
                        }
                    });

                    // Actualizar la gráfica y las leyendas
                    chart.update();
                }
                // onClick: (e, legendItem, legend) => {
                //     const chart = legend.chart;
                //     const datasetIndex = legendItem.datasetIndex;
                //     const dataset = chart.data.datasets[datasetIndex];
                //     const deviceName = dataset.label.replace(' (Anterior)', '');

                //     // Alternar visibilidad del dataset sin actualizar el estado global
                //     dataset.hidden = !dataset.hidden;

                //     // También ocultar su versión anterior si `showComparison` está activo
                //     if (showChartComparison) {
                //         const comparisonDataset = chart.data.datasets.find(ds => ds.label === `${deviceName} (Anterior)`);
                //         if (comparisonDataset) {
                //             comparisonDataset.hidden = dataset.hidden;
                //         }
                //     }

                //     chart.update(); // ✅ Actualiza solo la gráfica sin afectar el estado global
                // },

            },
            tooltip: {
                callbacks: {
                    title: function (tooltipItems) {
                        // Solo tomamos el primer elemento del array tooltipItems
                        const tooltipItem = tooltipItems[0];
                        const index = tooltipItem.dataIndex;
                        const isPreviousPeriod = tooltipItem.dataset.label.includes('(Anterior)');
                        const dateLabels = filteredData.map(item => formatDate(item.date));
                        const previousDates = filteredPreviousData.map(item => formatDate(item.date));

                        // Obtener la fecha correspondiente
                        const dateLabel = isPreviousPeriod
                            ? previousDates[index] + ' (Previo)' // Si es el periodo anterior, usa `previousDates`
                            : dateLabels[index];       // Si es el actual, usa `labels`

                        return dateLabel;
                    },
                    label: function (tooltipItem) {
                        const dataset = tooltipItem.dataset.label.replace(/(\(Actual\)|\(Anterior\)|\(En ralentí\))/g, '').trim();

                        const parameter = tooltipItem.dataset.parameter;
                        const value = tooltipItem.raw;

                        setValuesPeriod(dataset);

                        return `${dataset} - ${parameter}: ${formatTime(value)}`;
                    }
                }
            }
        }
    };


    // Configuración de datos para cada gráfico de pastel
    const generatePieData = () => {
        const currentPeriod = chartDateRange === 0 ? 'Hoy' : chartDateRange === 1 ? 'Semana actual' : 'Mes actual';
        const previousPeriod = chartDateRange === 0 ? 'Ayer' : chartDateRange === 1 ? 'Semana previa' : 'Mes previo';
        const currentData = getDeviceDataForPeriod(currentPeriod);
        const previousData = showChartComparison ? getDeviceDataForPeriod(previousPeriod) : null;

        return {
            labels: ['En Movimiento', 'En Ralentí', 'Motor apagado'],
            datasets: [
                {
                    label: `${currentPeriod}`,
                    data: [currentData.in_movement, currentData.idle, currentData.engine_off_time],
                    backgroundColor: ['#36A2EB', '#FF6384', '#FF9F40'],
                    hoverBackgroundColor: ['#36A2EB', '#FF6384', '#FF9F40']
                },
                ...(showChartComparison ? [{
                    label: `${previousPeriod}`,
                    data: [previousData.in_movement, previousData.idle, previousData.engine_off_time],
                    backgroundColor: ['#36A2EB', '#FF6384', '#FF9F40'],
                    hoverBackgroundColor: ['#36A2EB', '#FF6384', '#FF9F40']
                }] : [])
            ]
        };
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        animation: {
            animateRotate: true, // Activa la animación de rotación
            // animateScale: true,
            duration: 2000,
            easing: 'easeOutBounce',
        },
        plugins: {
            legend: {
                onHover: (event) => {
                    event.native.target.style.cursor = 'pointer';
                },
                onLeave: (event) => {
                    event.native.target.style.cursor = 'default';
                },
                position: 'bottom',
            },
            title: {
                display: false,
                text: 'Totales de actividad del vehículo'
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (tooltipItem) {
                        const dataset = tooltipItem.dataset.label;
                        const value = tooltipItem.raw;

                        // Formatea el texto del tooltip
                        return `${dataset}: ${formatTime(value)} `;
                    }
                }
            }
        }
    };

    const calculatePeriodTotalData = (period, car, dataKey) => {
        const periodData = totalData[period];

        if (periodData && periodData[car] && periodData[car][dataKey]) {
            return periodData[car][dataKey]; // Retornar el valor correcto (mileage, time, etc.)
        }

        return 0; // Si no hay datos, retornar 0
    };

    const setValuesPeriod = (dataset) => {
        // Obtener la opción seleccionada del selector
        const selectedOption = chartDateRange;
        let currentPeriodRalenti, currentPeriodInMovement, previousPeriodRalenti, previousPeriodInMovement, currentPeriodDates, previousPeriodDates;
        // Actualizar los spans con el kilometraje total y las fechas de los periodos
        if (selectedOption === 0) {
            currentPeriodRalenti = calculatePeriodTotalData('Hoy', dataset, 'idle');
            currentPeriodInMovement = calculatePeriodTotalData('Hoy', dataset, 'in_movement');
            previousPeriodRalenti = calculatePeriodTotalData('Ayer', dataset, 'idle');
            previousPeriodInMovement = calculatePeriodTotalData('Ayer', dataset, 'in_movement');

            currentPeriodDates = { startDate: startOfDay(new Date(today)), endDate: today };
            previousPeriodDates = { startDate: yesterday, endDate: subtractDays(1, today, true) };
        } else if (selectedOption === 1) {
            currentPeriodRalenti = calculatePeriodTotalData('Semana actual', dataset, 'idle');
            currentPeriodInMovement = calculatePeriodTotalData('Semana actual', dataset, 'in_movement');
            previousPeriodRalenti = calculatePeriodTotalData('Semana previa', dataset, 'idle');
            previousPeriodInMovement = calculatePeriodTotalData('Semana previa', dataset, 'in_movement');

            currentPeriodDates = { startDate: startActualWeek, endDate: endActualWeek };
            previousPeriodDates = { startDate: startPreviousWeek, endDate: endPreviousWeek };
        } else if (selectedOption === 2) {
            currentPeriodRalenti = calculatePeriodTotalData('Mes actual', dataset, 'idle');
            currentPeriodInMovement = calculatePeriodTotalData('Mes actual', dataset, 'in_movement');
            previousPeriodRalenti = calculatePeriodTotalData('Mes previo', dataset, 'idle');
            previousPeriodInMovement = calculatePeriodTotalData('Mes previo', dataset, 'in_movement');

            currentPeriodDates = { startDate: startActualMonth, endDate: endActualMonth };
            previousPeriodDates = { startDate: startPreviousMonth, endDate: endPreviousMonth };
        }

        const currentPeriodElemRalenti = document.getElementById('current-period-ralenti');
        const currentPeriodElemInMovement = document.getElementById('current-period-in_movement');
        const previousPeriodElemRalenti = document.getElementById('previous-period-ralenti');
        const previousPeriodElemInMovement = document.getElementById('previous-period-in_movement');

        currentPeriodElemRalenti.innerText = `${formatTime(currentPeriodRalenti)}`;
        currentPeriodElemInMovement.innerText = `${formatTime(currentPeriodInMovement)}`;
        previousPeriodElemRalenti.innerText = `${formatTime(previousPeriodRalenti)}`;
        previousPeriodElemInMovement.innerText = `${formatTime(previousPeriodInMovement)}`;

        // Remover clases previas
        currentPeriodElemRalenti.classList.remove('high', 'normal', 'low');
        currentPeriodElemInMovement.classList.remove('high', 'normal', 'low');
        previousPeriodElemRalenti.classList.remove('high', 'normal', 'low');
        previousPeriodElemInMovement.classList.remove('high', 'normal', 'low');

        // Cambiar el className según la comparación
        if (currentPeriodRalenti > previousPeriodRalenti) {
            currentPeriodElemRalenti.classList.add('high');
            previousPeriodElemRalenti.classList.add('low');
        } else if (currentPeriodRalenti < previousPeriodRalenti) {
            currentPeriodElemRalenti.classList.add('low');
            previousPeriodElemRalenti.classList.add('high');
        } else {
            // Si son iguales, ambos valores tendrán la clase 'yellow'
            currentPeriodElemRalenti.classList.add('normal');
            previousPeriodElemRalenti.classList.add('normal');
        }

        if (currentPeriodInMovement > previousPeriodInMovement) {
            currentPeriodElemInMovement.classList.add('high');
            previousPeriodElemInMovement.classList.add('low');
        } else if (currentPeriodInMovement < previousPeriodInMovement) {
            currentPeriodElemInMovement.classList.add('low');
            previousPeriodElemInMovement.classList.add('high');
        } else {
            // Si son iguales, ambos valores tendrán la clase 'yellow'
            currentPeriodElemInMovement.classList.add('normal');
            previousPeriodElemInMovement.classList.add('normal');
        }

        // Formatear las fechas en dd/mm/aaaa - dd/mm/aaaa
        if (selectedOption === 0) {
            document.getElementById('current-period-date-ralenti').innerText = `(${formatDate(currentPeriodDates.startDate)})`;
            document.getElementById('previous-period-date-ralenti').innerText = `(${formatDate(previousPeriodDates.startDate)})`;
        } else {
            document.getElementById('current-period-date-ralenti').innerText = `(${formatDate(currentPeriodDates.startDate)} - ${formatDate(currentPeriodDates.endDate)})`;
            document.getElementById('previous-period-date-ralenti').innerText = `(${formatDate(previousPeriodDates.startDate)} - ${formatDate(previousPeriodDates.endDate)})`;
        }
    };

    const resetZoom = () => {
        if (barChartRef.current) {
            barChartRef.current.resetZoom(); // Usar la referencia para resetear el zoom
        }
    };

    const showZoomMessage = useRef(false);
    const [, forceRender] = useReducer(x => x + 1, 0);
    const zoomTimeout = useRef(null);


    const handleWheel = (e) => {
        if (chartDateRange > 0 && !e.ctrlKey) {
            if (!showZoomMessage.current) {
                showZoomMessage.current = true;
                forceRender();

                if (zoomTimeout.current) clearTimeout(zoomTimeout.current);
                zoomTimeout.current = setTimeout(() => {
                    showZoomMessage.current = false;
                    forceRender();
                }, 3000);
            }
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'bar':
                return <>
                    <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
                        <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
                            Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
                        </div>
                        <Bar ref={barChartRef} data={generateStackedBarDataByDate()} options={stackedBarOptionsByDate} />
                    </div>
                    <ChartLegends devices={devices} updateDevices={updateDevices} id={'ralenti-bar-'} />
                </>
            case 'pie':
                return renderPieChart();  // Mantener la gráfica de pastel
            default:
                return null;
        }
    };


    // Actualiza los datos actuales y anteriores de la tabla
    useEffect(() => {
        if (activeTab === 'pie') {
            const { current, previous } = periodKeyMap[chartDateRange];
            const currentPeriodData = totalData[current]?.[selectedDevice] || emptyData;
            const previousPeriodData = totalData[previous]?.[selectedDevice] || emptyData;

            setCurrentData(currentPeriodData);
            setPreviousData(previousPeriodData);
        }
    }, [activeTab, selectedDevice, chartDateRange]);


    const handleCheckboxChange = () => {
        setShowChartComparison(!showChartComparison); // Alternar el estado
    };

    const handleShowAllHigherTopToggle = () => {
        setShowAllHigherTop(!showAllHigherTop);
    };

    const handleShowAllLesserTopToggle = () => {
        setShowAllLesserTop(!showAllLesserTop);
    };

    const handleShowAllTopRalentiFuelToggle = () => {
        setShowAllTopRalentiFuel(!showAllTopRalentiFuel);
    };

    const getDataByDateRange = (range) => {
        let periodCurrentData = {}, periodPreviousData = {};

        switch (range) {
            case 0:
                periodCurrentData = totalData['Hoy'] || {};
                periodPreviousData = totalData['Ayer'] || {};
                break;
            case 1:
                periodCurrentData = totalData['Semana actual'] || {};
                periodPreviousData = totalData['Semana previa'] || {};
                break;
            case 2:
                periodCurrentData = totalData['Mes actual'] || {};
                periodPreviousData = totalData['Mes previo'] || {};
                break;
            default:
                return [];
        }

        // Filtramos dispositivos visibles
        const visibleDevices = devices.filter(device => device.visible);

        // Filtrar dispositivos visibles y mapear los datos solo para esos dispositivos
        const combinedData = Object.keys({ ...periodCurrentData, ...periodPreviousData })
            .filter(car => visibleDevices.find(device => device.label === car))  // Filtra solo los visibles
            .map((car) => {
                const current = periodCurrentData[car] || emptyData;
                const previous = periodPreviousData[car] || emptyData;

                return {
                    car, // Nombre del vehículo
                    currentDuration: current.duration,
                    currentIdle: current.idle,
                    currentAvgPeriod: current.avg_period,
                    currentAvgSpeed: current.avg_speed,
                    currentInMovement: current.in_movement,
                    currentPercentInMovement: current.percent_in_movement,
                    currentPercentIdle: current.percent_idle,
                    currentMileage: current.mileage,
                    currentPeriodCount: current.period_count,

                    previousDuration: previous.duration,
                    previousIdle: previous.idle,
                    previousAvgPeriod: previous.avg_period,
                    previousAvgSpeed: previous.avg_speed,
                    previousInMovement: previous.in_movement,
                    previousPercentInMovement: previous.percent_in_movement,
                    previousPercentIdle: previous.percent_idle,
                    previousMileage: previous.mileage,
                    previousPeriodCount: previous.period_count,
                };
            });

        return combinedData;
    };

    // useEffect para ordenar por ralentí alto
    useEffect(() => {
        const sortedByRalentiHigher = getDataByDateRange(dateHigherTopRange).sort((a, b) => {
            if (b.currentIdle === a.currentIdle) {
                return b.previousIdle - a.previousIdle;
            }
            return b.currentIdle - a.currentIdle;
        });

        setDisplayedHigherData(showAllHigherTop ? sortedByRalentiHigher : sortedByRalentiHigher.slice(0, 5));
    }, [devices, totalData, dateHigherTopRange, showAllHigherTop]);

    // useEffect para ordenar por ralentí bajo
    useEffect(() => {
        const sortedByRalentiLesser = getDataByDateRange(dateLesserTopRange).sort((a, b) => {
            if (a.currentIdle === b.currentIdle) {
                return a.previousIdle - b.previousIdle;
            }
            return a.currentIdle - b.currentIdle;
        });

        setDisplayedLesserData(showAllLesserTop ? sortedByRalentiLesser : sortedByRalentiLesser.slice(0, 5));
    }, [devices, totalData, dateLesserTopRange, showAllLesserTop]);

    // useEffect para ordenar por consumo de combustible relacionado con ralentí
    useEffect(() => {
        // const sortedByRalentiFuel = getDataByDateRange(dateTopRangeRalentiFuel);
        setDisplayedTopRalentiFuel(getDataByDateRange(dateTopRangeRalentiFuel));
    }, [devices, totalData, dateTopRangeRalentiFuel]);


    const totalItems = displayedHigherData.length;

    // Calcular el ancho basado en la cantidad de dígitos
    const indexWidth = totalItems < 10 ? '2ch' : totalItems < 100 ? '3ch' : '4ch';

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const renderPieChart = () => {
        return (
            <div className='row_content'>
                <div className='config_chart_container'>
                    <div>
                        <select
                            value={selectedDevice} // Para mantener la selección actualizada
                            onChange={(e) => setSelectedDevice(e.target.value)}
                            className='selector'
                            style={{ width: '100%' }}
                        >
                            {deviceLabels.map((label, index) => (
                                <option key={index} value={label}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='chart_bottom_container'>
                        <table className='period_table'>
                            <thead>
                                <tr>
                                    <th>Parámetro</th>
                                    <th>Periodo actual <br /> <span className='period_date'>({chartDateRange === 0 ? formatDate(today) : chartDateRange === 1 ? (formatDate(startActualWeek) + ' - ' + formatDate(today)) : (formatDate(startActualMonth) + ' - ' + formatDate(today))})</span></th>
                                    <th>Periodo anterior <br /> <span className='period_date'>({chartDateRange === 0 ? formatDate(yesterday) : chartDateRange === 1 ? (formatDate(startPreviousWeek) + ' - ' + formatDate(endPreviousWeek)) : (formatDate(startPreviousMonth) + ' - ' + formatDate(endPreviousMonth))})</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Horas del motor</td>
                                    <td className={`${currentData.duration > previousData.duration ? 'high' : currentData.duration < previousData.duration ? 'low' : 'normal'}`}>{formatTime(currentData.duration)}</td>
                                    <td className={`${currentData.duration < previousData.duration ? 'high' : currentData.duration > previousData.duration ? 'low' : 'normal'}`}>{formatTime(previousData.duration)}</td>
                                </tr>
                                <tr>
                                    <td>En movimiento</td>
                                    <td className={`${currentData.in_movement > previousData.in_movement ? 'high' : currentData.in_movement < previousData.in_movement ? 'low' : 'normal'}`}>{formatTime(currentData.in_movement)} ({currentData.percent_in_movement.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)</td>
                                    <td className={`${currentData.in_movement < previousData.in_movement ? 'high' : currentData.in_movement > previousData.in_movement ? 'low' : 'normal'}`}>{formatTime(previousData.in_movement)} ({previousData.percent_in_movement.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)</td>
                                </tr>
                                <tr>
                                    <td>Ralentí</td>
                                    <td className={`${currentData.idle > previousData.idle ? 'high' : currentData.idle < previousData.idle ? 'low' : 'normal'}`}>{formatTime(currentData.idle)} ({currentData.percent_idle.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)</td>
                                    <td className={`${currentData.idle < previousData.idle ? 'high' : currentData.idle > previousData.idle ? 'low' : 'normal'}`}>{formatTime(previousData.idle)} ({previousData.percent_idle.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)</td>
                                </tr>
                                <tr>
                                    <td>Intervalo promedio</td>
                                    <td className={`${currentData.avg_period > previousData.avg_period ? 'high' : currentData.avg_period < previousData.avg_period ? 'low' : 'normal'}`}>{formatTime(currentData.avg_period)}</td>
                                    <td className={`${currentData.avg_period < previousData.avg_period ? 'high' : currentData.avg_period > previousData.avg_period ? 'low' : 'normal'}`}>{formatTime(previousData.avg_period)}</td>
                                </tr>
                                <tr>
                                    <td>Kilometraje</td>
                                    <td className={`${currentData.mileage > previousData.mileage ? 'high' : currentData.mileage < previousData.mileage ? 'low' : 'normal'}`}>{currentData.mileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km</td>
                                    <td className={`${currentData.mileage < previousData.mileage ? 'high' : currentData.mileage > previousData.mileage ? 'low' : 'normal'}`}>{previousData.mileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km</td>
                                </tr>
                                <tr>
                                    <td>Velocidad promedio</td>
                                    <td className={`${currentData.avg_speed > previousData.avg_speed ? 'high' : currentData.avg_speed < previousData.avg_speed ? 'low' : 'normal'}`}>{currentData.avg_speed.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km/h</td>
                                    <td className={`${currentData.avg_speed < previousData.avg_speed ? 'high' : currentData.avg_speed > previousData.avg_speed ? 'low' : 'normal'}`}>{previousData.avg_speed.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km/h</td>
                                </tr>
                                <tr>
                                    <td>Intervalos</td>
                                    <td className={`${currentData.period_count > previousData.period_count ? 'high' : currentData.period_count < previousData.period_count ? 'low' : 'normal'}`}>{currentData.period_count.toLocaleString('en-Us', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                                    <td className={`${currentData.period_count < previousData.period_count ? 'high' : currentData.period_count > previousData.period_count ? 'low' : 'normal'}`}>{previousData.period_count.toLocaleString('en-Us', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className='multi_chart_container'>
                    <div className='pie_chart_container'>
                        {/* {currentData.duration === 0 ? (showChartComparison ? (previousData.duration === 0 ? <span>Sin datos para <br />este periodo</span> : '') : <span>Sin datos para <br />este periodo</span>) : ''} */}
                        {(currentData.duration === 0 || showChartComparison ? previousData.duration === 0 : false) ? (
                            <span className="text_no_vehicles" style={{ fontSize: '0.8rem', maxWidth: '11rem', marginTop: '-8px' }}>No se encontraron datos para este periodo</span>
                        ) : ''}
                        <Pie ref={chartRef} data={generatePieData()} options={options} />
                    </div>
                </div>
            </div>
        );
    };

    const [cardsReady, setCardsReady] = useState([]);

    useEffect(() => {
        const totalCards = 4; // Cambiar este número según la cantidad de tarjetas
        if (progress === 100) {
            // Generar tiempos aleatorios para cada tarjeta
            const randomDelays = Array.from({ length: totalCards }, () => Math.random() * 2000 + 1000); // 1-3 segundos
            const timeouts = randomDelays.map((delay, index) => {
                return setTimeout(() => {
                    setCardsReady((prev) => {
                        const updated = [...prev];
                        updated[index] = true;
                        return updated;
                    });
                }, delay);
            });

            // Limpiar los timeouts al desmontar
            return () => timeouts.forEach((timeout) => clearTimeout(timeout));
        } else {
            setCardsReady(new Array(totalCards).fill(false));
        }
    }, [progress]);

    return (
        <div className='section_grid_container'>
            <LargeCard
                variant='ralenti'
                dateRange={chartDateRange}
                setDateRange={setChartDateRange}
                showComparison={showChartComparison}
                setShowChartComparison={setShowChartComparison}
                handleCheckboxChange={handleCheckboxChange}
                handleTabChange={handleTabChange}
                renderContent={renderContent}
                activeTab={activeTab}
                resetZoom={resetZoom}
                progress={progress}
                isReady={!cardsReady[0]}
                error={error}
                fetchReport={fetchReport}
                sectionNum={3}
            />
            <div className='dashboard_card card_top card_short'>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={3} />
                    :
                    progress < 100 || !cardsReady[1] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='card_header_flex'>
                                <div className='dashboard_card_header_container'>
                                    <span className='dashboard_card_title'>{showAllHigherTop ? 'Todos los' : 'Top 5'} vehículos con mayor tiempo en ralentí</span>
                                    <button className='dashboard_card_view_more' onClick={handleShowAllHigherTopToggle}>
                                        {showAllHigherTop ? 'Ver menos' : 'Ver todos'}
                                    </button>
                                </div>
                                <div className='card_config_container'>
                                    <DateRangeSelector dateRange={dateHigherTopRange} setDateRange={setDateHigherTopRange} />
                                </div>
                            </div>
                            <div className='dashboard_card_content_container'>
                                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                    <div className='top_items_header_container'>
                                        <span>Periodo</span>
                                        <span>Actual<br /><span>({formatDateRange(dateHigherTopRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                        <span>Previo<br /><span>({formatDateRange(dateHigherTopRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                    </div>
                                    {
                                        displayedHigherData.length === 0 ?
                                            <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                            :
                                            displayedHigherData.map((item, index) => {
                                                return (
                                                    <ItemList
                                                        key={index}
                                                        index={index + 1}
                                                        type={'ralenti'}
                                                        name={item.car} // Nombre del vehículo
                                                        data={{
                                                            currentIdle: item.currentIdle, // Restar la duración actual del total del periodo
                                                            previousIdle: item.previousIdle // Restar la duración previa del total del periodo
                                                        }}
                                                    />
                                                );
                                            })
                                    }
                                </div>
                            </div>
                        </>
                    )}
            </div>
            <div className='dashboard_card card_top card_short'>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={3} />
                    :
                    progress < 100 || !cardsReady[2] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='card_header_flex'>
                                <div className='dashboard_card_header_container'>
                                    <span className='dashboard_card_title'>{showAllLesserTop ? 'Todos los' : 'Top 5'} vehículos con menor tiempo en ralentí</span>
                                    <button className='dashboard_card_view_more' onClick={handleShowAllLesserTopToggle}>
                                        {showAllLesserTop ? 'Ver menos' : 'Ver todos'}
                                    </button>
                                </div>
                                <div className='card_config_container'>
                                    <DateRangeSelector dateRange={dateLesserTopRange} setDateRange={setDateLesserTopRange} />
                                </div>
                            </div>
                            <div className='dashboard_card_content_container'>
                                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                    <div className='top_items_header_container'>
                                        <span>Periodo</span>
                                        <span>Actual<br /><span>({formatDateRange(dateLesserTopRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                        <span>Previo<br /><span>({formatDateRange(dateLesserTopRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                    </div>
                                    {
                                        displayedLesserData.length === 0 ?
                                            <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                            :
                                            displayedLesserData.map((item, index) => {
                                                return (
                                                    <ItemList
                                                        key={index}
                                                        index={index + 1}
                                                        type={'ralenti'}
                                                        name={item.car} // Nombre del vehículo
                                                        data={{
                                                            currentIdle: item.currentIdle,
                                                            previousIdle: item.previousIdle
                                                        }}
                                                    />
                                                );
                                            })
                                    }
                                </div>
                            </div>
                        </>
                    )}
            </div>
            <div className='dashboard_card card_top_large card_short' style={{ height: 'auto' }}>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={3} />
                    :
                    progress < 100 || !cardsReady[3] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='card_header_flex'>
                                <div className='dashboard_card_header_container'>
                                    <span className='dashboard_card_title'>Estimado de combustible perdido en ralentí</span>
                                    <button className='dashboard_card_view_more' onClick={handleShowAllTopRalentiFuelToggle}>
                                        {showAllTopRalentiFuel ? 'Ver menos' : 'Ver todos'}
                                    </button>
                                </div>
                                <div className='card_config_container'>
                                    <DateRangeSelector dateRange={dateTopRangeRalentiFuel} setDateRange={setDateTopRangeRalentiFuel} />
                                </div>
                            </div>
                            <div className='dashboard_card_content_container' style={{ height: '100%' }}>
                                <FuelEstimateCard devices={devices} displayedTopRalentiFuel={displayedTopRalentiFuel} showAllTopRalentiFuel={showAllTopRalentiFuel} userConstants={userConstants} updateUserConstants={updateUserConstants} />
                            </div>
                        </>
                    )}
            </div>
            {/* <div style={{ height: '200px', overflow: 'auto' }} onClick={(e) => {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(e.target);
          selection.removeAllRanges();
          selection.addRange(range);
        }}><pre>{JSON.stringify(dataReport, 0, 2)}</pre></div> */}
        </div>
    )
}

export default Ralenti
